import {
  OptionsObject,
  SnackbarKey,
  SnackbarMessage,
  useSnackbar,
} from "notistack";
import { useCallback } from "react";
import { AppVariant } from "../types";
import { useAppVariant } from "./useAppVariant";

interface UseToastOptions {
  variant?: AppVariant;
}

export const useToast = ({
  variant: overriddenVariant,
}: UseToastOptions = {}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { variant: appVariant } = useAppVariant();
  const variant = overriddenVariant || appVariant;

  /**
   * Creates a toast with the default options.
   */
  const toast = useCallback(
    (message: SnackbarMessage, options?: OptionsObject): SnackbarKey => {
      return enqueueSnackbar(message, {
        variant: "success",
        hideIconVariant: true,
        anchorOrigin: {
          horizontal: "center",
          vertical: variant === "mobile" ? "bottom" : "top",
        },
        className: "justify-center z-[2001] font-secondary",
        autoHideDuration: 3000,
        ...options,
      });
    },
    [enqueueSnackbar, variant]
  );

  /**
   * Creates a toast that can be interacted with by clicking on it.
   */
  const interactiveToast = useCallback(
    (message: SnackbarMessage, onClick: () => void) => {
      const toastId = toast(message, {
        SnackbarProps: {
          style: {
            cursor: "pointer",
            userSelect: "none",
          },
          onClick: () => {
            onClick();
            closeSnackbar(toastId);
          },
        },
      });
    },
    [closeSnackbar, toast]
  );

  return { toast, interactiveToast, closeToast: closeSnackbar };
};
